import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useCsvActions } from '../context/CsvActionsContext';
import { useT } from '../i18n';
import { parseCsv } from '../utils/csvParser';
import { Capability, Resource, DOMAIN_CAT } from '../types';
import { ClipboardList, X, RotateCcw, Check } from 'lucide-react';

const NATURE_ORDER: Capability['nature'][] = [
  'Assess',
  'Decide/Design',
  'Deliver/Execute',
  'Communicate/Report',
  'Relate',
  'Improve',
];

const DOMAIN_ORDER: Capability['domain'][] = [
  'Participant',
  'Mandant',
  'Animateur',
  'Organisation',
  'External Environment',
];

interface CapabilityWithStats extends Capability {
  totalResources: number;
  totalUsers: number;
  resourceStats: Array<{
    resourceId: number;
    criticalityMedian: number;
    maturityMedian: number;
  }>;
  qualifiedCount: number;
  isQualified: boolean;
}

interface ResourceWithScores extends Resource {
  criticalityMedian: number;
  criticalityMin: number;
  criticalityMax: number;
  maturityMedian: number;
  maturityMin: number;
  maturityMax: number;
  criticalityContributors: Array<{ userId: number; name: string; initials: string; score: number }>;
  maturityContributors: Array<{ userId: number; name: string; initials: string; score: number }>;
}

interface ResourceSelection {
  resourceId: number;
  selected: boolean;
}

// Helper function to get maturity color (greenness based on 1-5 scale)
function getMaturityColor(maturity: number): string {
  if (maturity <= 0) return 'var(--bg-inset)';
  // Map 1-5 to green intensity
  const intensity = (maturity / 5) * 100;
  return `hsl(142, 70%, ${85 - intensity * 0.5}%)`;
}

// Helper function to get criticality color (amber/yellow based on 0-1 scale)
function getCriticalityColor(criticality: number): string {
  if (criticality <= 0) return '#fde9a8';
  // Map 0-1 to amber intensity: 0 = light yellow, 1 = dark amber
  const lightness = 85 - criticality * 45; // 85% (light) down to 40% (dark)
  return `hsl(38, 85%, ${lightness}%)`;
}

// Criticality bucket definitions (high to low)
const CRIT_BUCKETS = [
  { min: 0.8, max: 1.0, label: '0.8–1.0', color: 'var(--crit-high)' },
  { min: 0.6, max: 0.8, label: '0.6–0.8', color: 'var(--crit-med-high)' },
  { min: 0.4, max: 0.6, label: '0.4–0.6', color: 'var(--crit-med)' },
  { min: 0.2, max: 0.4, label: '0.2–0.4', color: 'var(--crit-med-low)' },
  { min: 0.0, max: 0.2, label: '0.0–0.2', color: 'var(--crit-low)' },
];

// Stacked Bar for overview cards — matches ConsensusStackedBar design language
function CriticalityStackedBar({
  resources,
  height = 22,
}: {
  resources: Array<{ resourceId: number; criticalityMedian: number; maturityMedian: number }>;
  height?: number;
}) {
  const { t } = useT();
  if (resources.length === 0) {
    return <div className="criticality-stacked-bar empty" style={{ height }}>{t('wmq.noResources')}</div>;
  }

  // Assign each resource to highest matching bucket
  const counts = CRIT_BUCKETS.map(() => 0);
  for (const r of resources) {
    const idx = CRIT_BUCKETS.findIndex(b => r.criticalityMedian >= b.min);
    if (idx >= 0) counts[idx]++;
  }

  const total = resources.length;

  return (
    <div className="criticality-stacked-bar" style={{ height }}>
      {counts.map((count, i) => {
        if (count === 0) return null;
        const widthPercent = (count / total) * 100;
        return (
          <div
            key={i}
            className="crit-bar-segment"
            style={{ width: `${widthPercent}%`, background: CRIT_BUCKETS[i].color, minWidth: '24px' }}
            title={`${CRIT_BUCKETS[i].label}: ${count} ${count !== 1 ? t('wmq.resources') : t('wmq.resource')}`}
          >
            <span className="crit-segment-count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// Resource Card Component for Qualification Modal — matches LinkQualificationPage aggregate view
function QualificationResourceCard({
  resource,
  isSelected,
  onToggle,
  isAdmin,
}: {
  resource: ResourceWithScores;
  isSelected: boolean;
  onToggle: () => void;
  isAdmin: boolean;
}) {
  const { t } = useT();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedClass = isSelected ? `selected selected-${resource.type?.toLowerCase() || 'aggregate'}` : '';
  const resourceDescription = (resource as any).description || null;

  const maturityColor = getMaturityColor(resource.maturityMedian);

  const handleShowTooltip = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.bottom + 6,
        left: Math.max(8, rect.right - 260),
      });
    }
    setShowTooltip(true);
  };

  const handleHideTooltip = () => {
    setShowTooltip(false);
    setTooltipPos(null);
  };

  return (
    <div
      className={`resource-card ${selectedClass} aggregate-view`}
      onClick={isAdmin ? onToggle : undefined}
      style={{ cursor: isAdmin ? 'pointer' : 'default', opacity: isSelected ? 1 : 0.65, position: 'relative' }}
    >
      {/* Info button in top-right corner */}
      {resourceDescription && (
        <button
          ref={buttonRef}
          className="resource-card-info"
          onClick={(e) => {
            e.stopPropagation();
            if (showTooltip) handleHideTooltip();
            else handleShowTooltip();
          }}
          onMouseEnter={handleShowTooltip}
          onMouseLeave={handleHideTooltip}
          title={t('wmq.showDescription')}
        >
          i
        </button>
      )}
      {/* Tooltip rendered via portal to escape stacking context */}
      {showTooltip && resourceDescription && tooltipPos && createPortal(
        <div
          className="resource-card-tooltip-portal"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          <div className="resource-card-tooltip-title">{resource.title}</div>
          <div className="resource-card-tooltip-desc">{resourceDescription}</div>
        </div>,
        document.body
      )}
      <div className="resource-card-title">{resource.title}</div>
      {/* Bottom band split: criticality (left) | maturity (right) */}
      <div className="resource-card-contributors" style={{ display: 'flex', padding: 0, background: 'none' }}>
        <div
          className="resource-card-band-half"
          style={{
            background: getCriticalityColor(resource.criticalityMedian),
            color: resource.criticalityMedian >= 0.8 ? 'white' : '#1a1a1a',
          }}
          title={t('wmq.tooltip.criticality').replace('{n}', resource.criticalityMedian.toFixed(2))}
        >
          {resource.criticalityMedian.toFixed(2)}
        </div>
        <div
          className="resource-card-band-half"
          style={{
            background: maturityColor,
            color: (85 - resource.maturityMedian * 10) < 52 ? 'white' : '#1a1a1a',
          }}
          title={t('wmq.tooltip.maturity').replace('{n}', resource.maturityMedian.toFixed(1))}
        >
          {resource.maturityMedian.toFixed(1)}
        </div>
      </div>
    </div>
  );
}

// Spectrum Detail Modal — resources distributed along criticality columns
function SpectrumModal({
  capability,
  onClose,
  isAdmin,
}: {
  capability: CapabilityWithStats;
  onClose: () => void;
  isAdmin: boolean;
}) {
  const { t } = useT();
  const [resources, setResources] = useState<ResourceWithScores[]>([]);
  const [selections, setSelections] = useState<Map<number, boolean>>(new Map());
  const [criticalityThreshold, setCriticalityThreshold] = useState<number>(0.5);
  const [sliderActive, setSliderActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [capability.id]);

  const loadData = async () => {
    try {
      const resourcesData = await api.get(`/weight-maturity-qualifications/capability/${capability.id}/resources`) as ResourceWithScores[];
      setResources(resourcesData);

      const selectionsData = await api.get(`/weight-maturity-qualifications/capability/${capability.id}/selections`) as Array<{ resource_id: number; selected: number }>;
      const selectionsMap = new Map<number, boolean>();
      selectionsData.forEach(s => selectionsMap.set(s.resource_id, s.selected === 1));
      setSelections(selectionsMap);
    } catch (err) {
      console.error('Failed to load resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    setSaving(true);
    try {
      const selectionsArray: ResourceSelection[] = [];
      selections.forEach((selected, resourceId) => {
        selectionsArray.push({ resourceId, selected });
      });

      await api.put(`/weight-maturity-qualifications/capability/${capability.id}/selections`, {
        selections: selectionsArray,
      });

      onClose();
    } catch (err) {
      console.error('Failed to save selections:', err);
      alert(t('wmq.alert.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSliderChange = (threshold: number) => {
    if (!isAdmin) return;
    setCriticalityThreshold(threshold);
    setSliderActive(true);

    const newSelections = new Map<number, boolean>();
    resources.forEach(resource => {
      newSelections.set(resource.id, resource.criticalityMedian >= threshold);
    });
    setSelections(newSelections);
  };

  const toggleResource = (resourceId: number) => {
    if (!isAdmin) return;
    setSliderActive(false);

    const newSelections = new Map(selections);
    const currentValue = newSelections.get(resourceId) || false;
    newSelections.set(resourceId, !currentValue);
    setSelections(newSelections);
  };

  const selectedCount = Array.from(selections.values()).filter(v => v).length;

  // Free-position resources along spectrum (high criticality = left, low = right)
  const CARD_W = 17; // card width as % of container (approx 170px / ~1000px content area)
  const CARD_H = 76; // approx card height px
  const CARD_GAP = 8;

  const sortedResources = [...resources].sort((a, b) => b.criticalityMedian - a.criticalityMedian);
  const lanes: Array<Array<{ resource: ResourceWithScores; leftPct: number }>> = [];
  for (const resource of sortedResources) {
    const leftPct = (1 - resource.criticalityMedian) * (100 - CARD_W);
    let placed = false;
    for (const lane of lanes) {
      const overlaps = lane.some(item =>
        leftPct < item.leftPct + CARD_W && leftPct + CARD_W > item.leftPct
      );
      if (!overlaps) {
        lane.push({ resource, leftPct });
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([{ resource, leftPct }]);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-full wm-spectrum-modal" onClick={e => e.stopPropagation()}>
        {/* Capability Card Header */}
        <div className="detail-card" style={{ margin: 0, borderRadius: 'var(--r2) var(--r2) 0 0', borderBottom: '1px solid var(--line)' }}>
          <button className="btn-icon" onClick={onClose} style={{ position: 'absolute', top: 'var(--s16)', right: 'var(--s16)' }}><X className="icon-sm" /></button>
          <div className="detail-card-header">
            <h4 className="detail-card-title">{capability.title}</h4>
            <div className="detail-card-header-tags" style={{ marginRight: '40px' }}>
              <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[capability.domain] || 1}`}>{capability.domain}</span>
              <span className="meta-tag">{capability.nature}</span>
            </div>
          </div>
          <div className="detail-card-body">
            <p className="detail-card-description" style={{ maxWidth: Math.min(900, Math.max(300, Math.ceil((capability.description || '').length / 3 * 7))) }}>
              {capability.description || t('wmq.noDescription')}
            </p>
            <span className="detail-card-meta">
              {(capability.totalResources !== 1 ? t('wmq.resourcesQualified.other') : t('wmq.resourcesQualified.one')).replace('{n}', String(capability.totalResources))}
            </span>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="flex items-center justify-center" style={{ height: 200 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              {/* Criticality Threshold Control */}
              <div className={`qual-threshold ${!sliderActive ? 'inactive' : ''} ${!isAdmin ? 'view-only' : ''}`}>
                <div className="qual-threshold-row">
                  <label className="qual-threshold-label">{t('wmq.threshold.label').replace('{n}', criticalityThreshold.toFixed(2))}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={criticalityThreshold}
                    onChange={e => handleSliderChange(parseFloat(e.target.value))}
                    disabled={!isAdmin}
                    className="qual-threshold-slider"
                    style={{ direction: 'rtl' }}
                  />
                  <span className="qual-threshold-count">{t('wmq.threshold.selected').replace('{n}', String(selectedCount))}</span>
                  {isAdmin && (
                    <button
                      className="btn-icon"
                      title={t('wmq.resetSelections')}
                      onClick={() => {
                        setSelections(new Map());
                        setSliderActive(true);
                        setCriticalityThreshold(0.5);
                      }}
                      style={{ flexShrink: 0 }}
                    >
                      <RotateCcw className="icon-sm" />
                    </button>
                  )}
                </div>
                {!sliderActive && isAdmin && (
                  <div className="qual-threshold-hint">{t('wmq.threshold.manualOverride')}</div>
                )}
                {!isAdmin && (
                  <div className="qual-threshold-hint">{t('wmq.threshold.viewOnly')}</div>
                )}
              </div>

              {resources.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><ClipboardList /></div>
                  <div className="empty-state-title">{t('wmq.empty.title')}</div>
                  <div className="empty-state-desc">{t('wmq.empty.desc')}</div>
                </div>
              ) : (
                <div className="spectrum-free">
                  {/* Axis labels */}
                  <div className="spectrum-axis">
                    <span className="spectrum-axis-label">{t('wmq.axis.critical')}</span>
                    <span className="spectrum-axis-label">{t('wmq.axis.nonCritical')}</span>
                  </div>

                  {/* Gradient track */}
                  <div className="spectrum-track-bar">
                    {CRIT_BUCKETS.map((bucket, i) => (
                      <div key={i} className="spectrum-track-segment" style={{ background: bucket.color }} />
                    ))}
                  </div>

                  {/* Free-positioned resource cards */}
                  <div className="spectrum-free-area" style={{ height: lanes.length * (CARD_H + CARD_GAP) + CARD_GAP }}>
                    {lanes.map((lane, laneIdx) =>
                      lane.map(({ resource, leftPct }) => (
                        <div
                          key={resource.id}
                          className="spectrum-free-card"
                          style={{
                            left: `${leftPct}%`,
                            top: laneIdx * (CARD_H + CARD_GAP),
                          }}
                        >
                          <QualificationResourceCard
                            resource={resource}
                            isSelected={selections.get(resource.id) || false}
                            onToggle={() => toggleResource(resource.id)}
                            isAdmin={isAdmin}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{isAdmin ? t('common.cancel') : t('common.close')}</button>
          {isAdmin && (
            <button className="btn btn-accent" onClick={handleSave} disabled={saving}>
              {saving ? t('wmq.saving') : t('wmq.saveAndClose')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Capability Card Component for Grid
function CapabilityCard({
  capability,
  onClick,
}: {
  capability: CapabilityWithStats;
  onClick: () => void;
}) {
  const { t } = useT();
  return (
    <div className={`wm-qual-capability-card ${capability.isQualified ? 'qualified' : ''}`} onClick={onClick}>
      <div className="card-title">
        {capability.title}
        {capability.isQualified && (
          <span className="qualification-indicator" title={t('wmq.qualified')}><Check className="icon-sm" /></span>
        )}
      </div>

      {/* Resource count */}
      <div className="resource-count">
        {(capability.totalResources !== 1 ? t('wmq.resourceCount.other') : t('wmq.resourceCount.one')).replace('{n}', String(capability.totalResources))}
        {capability.qualifiedCount > 0 && (
          <span className="chosen-count"> | {t('wmq.chosen').replace('{n}', String(capability.qualifiedCount))}</span>
        )}
      </div>

      {/* Criticality stacked bar */}
      <CriticalityStackedBar resources={capability.resourceStats} />
    </div>
  );
}

export function MaturityQualificationPage() {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const { setCsvActions } = useCsvActions();
  const [capabilities, setCapabilities] = useState<CapabilityWithStats[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<CapabilityWithStats | null>(null);
  const [loading, setLoading] = useState(true);

  // CSV import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  // Space+drag panning (using 'V' key like LinkQualificationPage)
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const spaceHeldRef = useRef(false);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'KeyV' && !e.repeat && !spaceHeldRef.current && !(e.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) {
        spaceHeldRef.current = true;
        setSpaceHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'KeyV') {
        spaceHeldRef.current = false;
        isPanning.current = false;
        setSpaceHeld(false);
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!spaceHeldRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isPanning.current) return;
      el.scrollLeft = panStart.current.scrollLeft - (e.clientX - panStart.current.x);
      el.scrollTop = panStart.current.scrollTop - (e.clientY - panStart.current.y);
    };
    const onMouseUp = () => { isPanning.current = false; };
    const onClick = (e: MouseEvent) => {
      if (!spaceHeldRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    el.addEventListener('mousedown', onMouseDown, true);
    el.addEventListener('mousemove', onMouseMove, true);
    el.addEventListener('mouseup', onMouseUp, true);
    el.addEventListener('mouseleave', onMouseUp);
    el.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      el.removeEventListener('mousedown', onMouseDown, true);
      el.removeEventListener('mousemove', onMouseMove, true);
      el.removeEventListener('mouseup', onMouseUp, true);
      el.removeEventListener('mouseleave', onMouseUp);
      el.removeEventListener('click', onClick, true);
    };
  }, []);

  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const data = await api.get('/weight-maturity-qualifications/all-capabilities') as CapabilityWithStats[];
      setCapabilities(data);
    } catch (err) {
      console.error('Failed to load capabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      uploadLabel: t('wmq.importCsv'),
      onUpload: isAdmin ? () => fileInputRef.current?.click() : undefined,
      downloadUrl: '/api/weight-maturity-qualifications/export-csv',
      downloadLabel: t('wmq.exportCsv'),
      uploading: importing,
    });
    return () => setCsvActions(null);
  }, [importing, isAdmin, setCsvActions, t]);

  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const rows = parseCsv(text);
      if (rows.length === 0) {
        alert(t('wmq.alert.csvEmpty'));
        return;
      }
      executeImport(rows);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const executeImport = async (rows: Record<string, string>[]) => {
    setImporting(true);
    try {
      const result = await api.importWeightMaturityQualificationsCsv(rows) as { imported: number; skipped: number };
      alert(t('wmq.alert.importComplete').replace('{imported}', String(result.imported)).replace('{skipped}', String(result.skipped)));
      loadCapabilities();
    } catch (err: any) {
      alert(t('wmq.alert.importFailed') + err.message);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 400 }}>
        <div className="spinner" />
      </div>
    );
  }

  // Organize capabilities by domain and nature
  const capabilityGrid: Record<string, Record<string, CapabilityWithStats[]>> = {};
  DOMAIN_ORDER.forEach(domain => {
    capabilityGrid[domain] = {};
    NATURE_ORDER.forEach(nature => {
      capabilityGrid[domain][nature] = capabilities.filter(c => c.domain === domain && c.nature === nature);
    });
  });

  return (
    <div className="wm-qual-page">
      <style>{`
        .wm-qual-page {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        /* Capability cards within grid cells */
        .wm-qual-capability-card {
          background: var(--surface);
          border: 1px solid var(--line);
          border-radius: var(--r2);
          padding: var(--s16) var(--s16) var(--s12);
          cursor: pointer;
          transition: all var(--dt-mid) var(--ease);
          position: relative;
          display: flex;
          flex-direction: column;
          gap: var(--s8);
        }

        .wm-qual-capability-card:hover {
          border-color: var(--line);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.04);
        }

        [data-theme="dark"] .wm-qual-capability-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .wm-qual-capability-card.qualified {
          border-color: var(--ok);
        }

        .qualification-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--ok);
          color: white;
          font-size: 10px;
          font-weight: bold;
          margin-left: 6px;
          vertical-align: middle;
          flex-shrink: 0;
        }

        .wm-qual-capability-card .card-title {
          font-size: var(--t-15);
          font-weight: 600;
          color: var(--ink-1);
          line-height: 1.35;
          flex: 1;
        }

        .wm-qual-capability-card .resource-count {
          font-size: var(--t-12);
          font-family: var(--font-mono);
          font-weight: 500;
          color: var(--ink-3);
          margin-bottom: 6px;
        }

        .wm-qual-capability-card .chosen-count {
          color: var(--ok);
        }

        /* Criticality palette */
        .wm-qual-page {
          --crit-high: #b45309;
          --crit-med-high: #d97706;
          --crit-med: #e8a545;
          --crit-med-low: #f5c779;
          --crit-low: #fde9a8;
        }

        /* Criticality Stacked Bar (overview cards) */
        .criticality-stacked-bar {
          display: flex;
          width: 100%;
          height: 22px;
          border-radius: var(--r1);
          overflow: hidden;
          margin-top: auto;
          background: var(--bg-inset);
        }
        .criticality-stacked-bar.empty {
          color: var(--ink-3);
          font-size: var(--t-12);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .crit-bar-segment {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          overflow: hidden;
          transition: opacity var(--dt-fast);
        }
        .crit-bar-segment:hover { opacity: 0.85; }
        .crit-segment-count {
          font-size: 0.6875rem;
          font-family: var(--font-mono);
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.25);
          padding: 0 4px;
          white-space: nowrap;
        }
        [data-theme="dark"] .crit-segment-count {
          color: rgba(0,0,0,0.75);
          text-shadow: none;
        }

        /* Spectrum detail modal */
        .wm-spectrum-modal {
          max-width: 1100px;
        }

        .spectrum-free {
          margin-top: var(--s12);
        }

        .spectrum-axis {
          display: flex;
          justify-content: space-between;
          margin-bottom: var(--s4);
        }
        .spectrum-axis-label {
          font-size: var(--t-12);
          font-weight: 600;
          color: var(--ink-3);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .spectrum-track-bar {
          display: flex;
          height: 8px;
          border-radius: var(--r1);
          overflow: hidden;
          margin-bottom: var(--s12);
        }
        .spectrum-track-segment {
          flex: 1;
        }

        .spectrum-free-area {
          position: relative;
          width: 100%;
          min-height: 80px;
        }

        .spectrum-free-card {
          position: absolute;
        }
        .spectrum-free-card .resource-card {
          height: 76px;
        }
        .resource-card-band-half {
          flex: 1;
          text-align: center;
          font-size: 10px;
          font-weight: 500;
          font-family: var(--font-mono);
          padding: 2px 4px;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        /* Threshold slider */
        .qual-threshold {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: var(--s8) var(--s12);
          background: var(--bg-inset);
          border-radius: var(--r2);
          margin-bottom: var(--s12);
          transition: opacity var(--dt-fast);
        }
        .qual-threshold.inactive { opacity: 0.45; }
        .qual-threshold-row {
          display: flex;
          align-items: center;
          gap: var(--s12);
        }
        .qual-threshold-label {
          font-size: var(--t-12);
          font-weight: 600;
          font-family: var(--font-mono);
          color: var(--ink-3);
          white-space: nowrap;
          min-width: 130px;
        }
        .qual-threshold-slider {
          flex: 1;
          height: 4px;
          -webkit-appearance: none;
          appearance: none;
          background: var(--line);
          border-radius: 2px;
          outline: none;
          cursor: pointer;
        }
        .qual-threshold-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px; height: 14px;
          border-radius: 50%;
          background: var(--accent);
          border: 2px solid var(--surface);
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .qual-threshold-slider::-moz-range-thumb {
          width: 14px; height: 14px;
          border-radius: 50%;
          background: var(--accent);
          border: 2px solid var(--surface);
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .qual-threshold-count {
          font-size: var(--t-12);
          font-family: var(--font-mono);
          color: var(--ink-2);
          white-space: nowrap;
        }
        .qual-threshold-hint {
          font-size: 0.625rem;
          color: var(--ink-2);
          font-style: italic;
        }
        .qual-threshold.view-only { opacity: 0.7; }
        .qual-threshold.view-only .qual-threshold-slider { cursor: not-allowed; }

        /* Resource card tooltip in modal */
        .modal-full .resource-card { position: relative; }
        .modal-full .resource-card-info {
          position: absolute;
          top: 4px; right: 4px; bottom: auto;
          opacity: 0;
        }
        .modal-full .resource-card:hover .resource-card-info { opacity: 1; }

        /* Portal-based tooltip */
        .resource-card-tooltip-portal {
          position: fixed;
          z-index: 99999;
          width: 250px;
          padding: var(--s12);
          background: #ffffff;
          border: 1px solid var(--line);
          border-radius: var(--r2);
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          font-size: var(--t-12);
          line-height: 1.5;
          text-align: left;
          pointer-events: none;
        }
        .resource-card-tooltip-title {
          font-weight: 600;
          font-size: var(--t-13);
          color: #1a1a1a;
          margin-bottom: var(--s4);
          line-height: 1.3;
        }
        .resource-card-tooltip-desc {
          color: #555555;
        }
        [data-theme="dark"] .resource-card-tooltip-portal {
          background: #2a2a2a;
          border-color: #444444;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }
        [data-theme="dark"] .resource-card-tooltip-title {
          color: #f0f0f0;
        }
        [data-theme="dark"] .resource-card-tooltip-desc {
          color: #cccccc;
        }
      `}</style>

      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleCsvFileSelect}
      />

      <div
        className={`matrix-grid-container${spaceHeld ? ' panning-ready' : ''}`}
        ref={gridContainerRef}
      >
        <div className="matrix-grid" style={{ minWidth: '1300px' }}>
          <div className="matrix-grid-corner"></div>

          {NATURE_ORDER.map(nature => (
            <div key={nature} className="matrix-grid-col">
              {nature}
            </div>
          ))}

          {DOMAIN_ORDER.map(domain => (
            <React.Fragment key={domain}>
              <div className="matrix-grid-row">{domain}</div>
              {NATURE_ORDER.map(nature => {
                const capabilitiesInCell = capabilityGrid[domain][nature];
                return (
                  <div key={nature} className="matrix-grid-cell">
                    {capabilitiesInCell.map(capability => (
                      <CapabilityCard
                        key={capability.id}
                        capability={capability}
                        onClick={() => setSelectedCapability(capability)}
                      />
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Resource Selection Modal */}
      {selectedCapability && (
        <SpectrumModal
          capability={selectedCapability}
          onClose={() => {
            setSelectedCapability(null);
            loadCapabilities();
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
